import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { ProductObj } from "scrivito/Objs/Product/ProductObjClass"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"
import { MediaOption } from "shared/ui/media-option/MediaOption"

export interface ProductInputProps<T extends string = string>
  extends WithClassName {
  name?: string
  options: SelectOption<T, ProductObj>[] | undefined
  loading?: boolean
  disabled?: boolean
  elevated?: boolean
  renderValue?: () => React.ReactNode
  popoverClassName?: string
  popupClassName?: string
  label?: string
  placeholder?: string
  multiple?: boolean
  required?: boolean
  autoFocus?: boolean
}

export const ProductInput = React.memo(
  <T extends string = string>({
    name = "products",
    options,
    loading,
    disabled,
    elevated,
    renderValue,
    popoverClassName,
    popupClassName,
    label,
    placeholder,
    multiple = true,
    required,
    autoFocus,
    className
  }: ProductInputProps<T>) => {
    const { t } = useTranslation()
    const resolvedLabel = label ?? t("BuildingReference.Filter.Products")

    return (
      <Combobox.Root
        name={name}
        label={resolvedLabel}
        loading={loading}
        disabled={disabled}
        elevated={elevated}
        className={classNames("product-input", className)}
        items={options}
        multiple={multiple}
        required={required}
      >
        <Combobox.Label />
        <Combobox.Control>
          <Combobox.Trigger autoFocus={autoFocus}>
            <Combobox.Value placeholder={placeholder}>
              {renderValue}
            </Combobox.Value>
            <Combobox.Icon />
          </Combobox.Trigger>
          <Combobox.Portal>
            <Combobox.Positioner
              className={popoverClassName}
              align="start"
              sideOffset={8}
            >
              <Combobox.Popup
                aria-label={resolvedLabel}
                className={popupClassName}
              >
                <Combobox.InputContainer>
                  <Combobox.Input />
                  <Icon name={IconName.SEARCH} />
                </Combobox.InputContainer>
                <Combobox.Empty />
                <Combobox.List>
                  {(option: SelectOption<T, ProductObj>) => (
                    <Combobox.Item key={option.value} value={option}>
                      <Combobox.ItemIndicator />
                      <Combobox.ItemContent>
                        <MediaOption.Root variant="product">
                          <MediaOption.Media>{option.image}</MediaOption.Media>
                          <MediaOption.Label>
                            {option.data?.get("productType") ?? option.label}
                          </MediaOption.Label>
                          {option.data?.get("kind") ? (
                            <MediaOption.Subtext>
                              {option.data?.get("kind")}
                            </MediaOption.Subtext>
                          ) : null}
                        </MediaOption.Root>
                      </Combobox.ItemContent>
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Control>
        <Combobox.ErrorMessage />
      </Combobox.Root>
    )
  }
) as <T extends string = string>(
  props: ProductInputProps<T>
) => React.ReactElement
